import { useCallback, useMemo } from 'react';

import { voltarAPrimeiraPagina } from '@/lib/estado-tabela';
import { useAtualizarUrl } from '@/lib/url';
import {
  classificacaoNotaSchema,
  direcaoSchema,
  modeloDocumentoSchema,
  situacaoDocumentoSchema,
  statusClassificacaoSchema,
} from '@fiscoai/contracts';

import { type FiltrosDocumentos } from './api';

export type ChaveFiltro =
  | 'direcao'
  | 'modelo'
  | 'situacao'
  | 'statusClassificacao'
  | 'classificacao'
  | 'emitidaDe'
  | 'emitidaAte'
  | 'q';

/** O que "limpar filtros" não apaga. */
const MANTER = new Set(['doc', 'porPagina', 'ordenarPor', 'ordem']);

/** Filtros vivem na URL: a busca é compartilhável e sobrevive ao recarregar. */
export function useFiltrosDocumentos() {
  const { params, atualizar } = useAtualizarUrl();

  const filtros = useMemo<FiltrosDocumentos>(() => {
    const ler = (chave: ChaveFiltro) => params.get(chave) ?? undefined;
    const direcao = direcaoSchema.safeParse(ler('direcao'));
    const modelo = modeloDocumentoSchema.safeParse(ler('modelo'));
    const situacao = situacaoDocumentoSchema.safeParse(ler('situacao'));
    const status = statusClassificacaoSchema.safeParse(ler('statusClassificacao'));
    const classificacao = classificacaoNotaSchema.safeParse(ler('classificacao'));
    const resultado: FiltrosDocumentos = {};
    if (status.success) resultado.statusClassificacao = status.data;
    if (classificacao.success) resultado.classificacao = classificacao.data;
    if (direcao.success) resultado.direcao = direcao.data;
    if (modelo.success) resultado.modelo = modelo.data;
    if (situacao.success) resultado.situacao = situacao.data;
    const de = ler('emitidaDe');
    const ate = ler('emitidaAte');
    const q = ler('q');
    if (de) resultado.emitidaDe = de;
    if (ate) resultado.emitidaAte = ate;
    if (q) resultado.q = q;
    return resultado;
  }, [params]);

  const definir = useCallback(
    (chave: ChaveFiltro, valor: string | undefined) => {
      atualizar(
        (proximo) => {
          if (valor) proximo.set(chave, valor);
          else proximo.delete(chave);
          voltarAPrimeiraPagina(proximo);
        },
        { replace: true },
      );
    },
    [atualizar],
  );

  /** Limpa só os filtros — a nota aberta, a ordem e os itens por página continuam. */
  const limpar = useCallback(() => {
    atualizar(
      (proximo) => {
        for (const chave of [...proximo.keys()]) if (!MANTER.has(chave)) proximo.delete(chave);
      },
      { replace: true },
    );
  }, [atualizar]);

  return { filtros, definir, limpar };
}
